import type { PlacementBox } from './mineCartPlacement';
export type SensAvalanche = 'montee' | 'descente';
interface Salve {
  rochers: number;
  grosBloc: boolean;
}
const LISIBILITE_MINIMALE = 0.6;
export const ROCHERS_PAR_SALVE_MAX = 5;
export interface FormeScene {
  layout?: PlacementBox[];
  sol: {
    /** Hauteur de la ligne de contact (pieds du compagnon), en fraction de la scène. */
    hauteurRatio: number;
  };
  projectile: {
    /** Hauteur d'un projectile en vol, en fraction de la hauteur de scène. */
    tailleRatio: number;
  };
  pente: {
    /** Largeur du chemin, en fraction de la largeur de scène. */
    largeurRatio: number;
    /** Dénivelé du chemin, en fraction de la hauteur de scène. */
    hauteurRatio: number;
    /**
     * Le PIED du chemin (d'où part la montée), en fraction de la largeur de scène : de quoi le
     * caler sur un repère du fond peint. Absent : la marge calculée, qui laisse la place du but.
     */
    departRatio?: number;
  };
  /**
   * Le côté vers lequel la pente PEINTE monte (défaut : la gauche). Le chemin, le but et
   * l'éboulement suivent le décor : une montée vers la droite sur une pente qui monte vers la
   * gauche se lit comme un contresens.
   */
  montee?: 'gauche' | 'droite';
}

export function hauteurSurLaPente(progression: number, sens: SensAvalanche): number {
  const p = Math.max(0, Math.min(1, Number.isFinite(progression) ? progression : 0));
  return sens === 'montee' ? p : 1 - p;
}

export const TAILLE_COMPAGNON_MONTEE = 0.3;
/** Un rocher de salve, rapporté au gros bloc d'un QCM. */
export const ECHELLE_ROCHER_SALVE = 0.66;
/** Taille du but (refuge, ou village de la vallée), en fraction de la hauteur de scène. */
export const TAILLE_BUT = 0.34;

export function geometrieMontee(sceneW: number, sceneH: number, theme: FormeScene) {
  const scene = theme.layout?.find(b => b.name === 'scene');
  const box = (name: string) => {
    const b = theme.layout?.find(b => b.name === name);
    if (!scene || !b) return undefined;
    return {
      left: ((b.xPct - scene.xPct) / scene.wPct) * sceneW,
      top: ((b.yPct - scene.yPct) / scene.hPct) * sceneH,
      width: (b.wPct / scene.wPct) * sceneW,
      height: (b.hPct / scene.hPct) * sceneH,
    };
  };
  const pied = box('pied_pente'),
    sommet = box('sommet_pente'),
    compagnon = box('compagnon'),
    projectile = box('projectile'),
    depart = box('depart_rochers');
  const solY = pied?.top ?? sceneH * theme.sol.hauteurRatio;
  const compagnonTaille = compagnon
    ? Math.min(compagnon.width, compagnon.height)
    : sceneH * TAILLE_COMPAGNON_MONTEE;
  /** Le sens où la pente monte : −1 vers la gauche, +1 vers la droite. */
  const cote: -1 | 1 =
    pied && sommet ? (sommet.left >= pied.left ? 1 : -1) : theme.montee === 'droite' ? 1 : -1;
  /**
   * La marge entre le bord de la scène et le bas du chemin. Assez large pour y poser un BUT —
   * le bas est le bout de la descente, et la vallée doit y tenir. Quand le bas est à GAUCHE,
   * elle passe en plus à droite de la jauge (en haut à gauche de l'écran) : la salve naissait
   * sinon derrière sa plaque.
   */
  const x0 = Math.max(
    sceneW * (cote > 0 ? 0.17 : 0.06),
    sceneH * TAILLE_BUT + compagnonTaille * 0.25 + sceneW * 0.02
  );
  const penteW =
    pied && sommet ? Math.abs(sommet.left - pied.left) : sceneW * theme.pente.largeurRatio;
  const penteH = pied && sommet ? pied.top - sommet.top : sceneH * theme.pente.hauteurRatio;
  const blocH = projectile?.height ?? sceneH * theme.projectile.tailleRatio;
  const rocherH = blocH * ECHELLE_ROCHER_SALVE;
  /** Le pied du chemin, en x : déclaré par le thème, sinon au bord de la marge. */
  const bas = pied
    ? pied.left
    : theme.pente.departRatio !== undefined
      ? sceneW * theme.pente.departRatio
      : cote > 0
        ? x0 + compagnonTaille / 2
        : sceneW - x0 - compagnonTaille / 2;
  return {
    sceneW,
    sceneH,
    solY,
    compagnonTaille,
    cote,
    x0,
    bas,
    penteW,
    penteH,
    depart,
    butOverride: box('but'),
    blocH,
    blocW: blocH * 1.15,
    rocherH,
    rocherW: rocherH * 1.15,
    butTaille: sceneH * TAILLE_BUT,
  };
}

export type GeometrieMontee = ReturnType<typeof geometrieMontee>;

export interface PointScene {
  x: number;
  y: number;
}

/**
 * Les pieds du compagnon (bas-centre) à la marche `k` d'un chemin de `n` marches. Il MONTE
 * vers le refuge — du côté où la pente peinte monte (`cote`) — ou DESCEND vers la vallée. La
 * position le long de la pente est `hauteurSurLaPente` : 0 en bas, 1 en haut. La marche `n` est
 * au pied du but.
 */
export function piedsALaMarche(
  k: number,
  n: number,
  g: GeometrieMontee,
  sens: SensAvalanche
): PointScene {
  const t = n > 0 ? Math.max(0, Math.min(1, k / n)) : 0;
  const u = hauteurSurLaPente(t, sens);
  return { x: g.bas + g.cote * u * g.penteW, y: g.solY - u * g.penteH };
}

/** Le but — le refuge en haut, ou la vallée en bas —, posé au bout de la dernière marche. */
export function placeDuBut(
  g: GeometrieMontee,
  sens: SensAvalanche
): { left: number; top: number; width: number; height: number } {
  if (sens === 'montee' && g.butOverride) return g.butOverride;
  const fin = piedsALaMarche(1, 1, g, sens);
  const versLaDroite = fin.x > piedsALaMarche(0, 1, g, sens).x;
  const left = versLaDroite
    ? fin.x + g.compagnonTaille * 0.25
    : fin.x - g.compagnonTaille * 0.25 - g.butTaille;
  return { left, top: fin.y - g.butTaille * 0.92, width: g.butTaille, height: g.butTaille };
}

/**
 * La trajectoire d'un rocher : de (x0, y0) à (x1, y1) — le coin haut-gauche de sa boîte —, avec
 * des REBONDS. Il dévale la montagne, il ne tombe pas du ciel.
 */
export interface TrajectoireRocher {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  width: number;
  height: number;
  /** Nombre de rebonds sur le trajet (entier : le dernier finit posé sur la cible). */
  rebonds: number;
  /** Hauteur du premier rebond ; les suivants s'amortissent. */
  hauteurBond: number;
}

/**
 * D'où partent les pierres — celles qui visent le compagnon comme celles du décor : hors du
 * cadre, du côté où la pente monte, à la hauteur du sommet. Un seul point de départ : une pierre
 * qui frappe doit venir de là où l'on voit dévaler les autres, sinon on ne lit pas l'avalanche.
 */
export function departDeLaMontagne(i: number, largeur: number, g: GeometrieMontee): PointScene {
  const decalage = (i % 4) * g.sceneW * 0.02;
  return {
    x:
      g.cote < 0
        ? (g.depart?.left ?? 0) - largeur * 1.2 - decalage
        : (g.depart?.left ?? g.sceneW) + largeur * 0.2 + decalage,
    y: (g.depart?.top ?? g.sceneH * 0.04) + g.sceneH * (i % 3) * 0.07,
  };
}

/**
 * Le `i`-ième rocher d'une salve qui dévale sur le compagnon dont les pieds sont en `pieds`.
 * Il part du HAUT de la montagne — du même endroit que les pierres du décor, hors du cadre, du
 * côté où la pente monte — et dévale jusqu'au haut de son corps, les rochers d'une salve en
 * éventail autour de lui. Un gros bloc est seul, et plus gros.
 */
export function trajectoireRocher(
  i: number,
  salve: Salve,
  pieds: PointScene,
  g: GeometrieMontee,
  heightPct?: number,
  ratio?: number
): TrajectoireRocher {
  const height =
    heightPct === undefined
      ? salve.grosBloc
        ? g.blocH
        : g.rocherH
      : ((g.sceneH * heightPct) / 100) * (salve.grosBloc ? 1 : ECHELLE_ROCHER_SALVE);
  const width = height * (heightPct !== undefined ? (ratio ?? 1.15) : 1.15);
  const n = Math.max(1, salve.rochers);
  const decalage = i - (n - 1) / 2;
  const x1 = Math.max(0, Math.min(g.sceneW - width, pieds.x + decalage * width * 0.95 - width / 2));
  // Il arrive sur le HAUT DU CORPS : visé au-dessus de la tête, le rocher d'une marche haute
  // n'avait presque plus de chute à faire, et on ne le voyait qu'une fois sur lui.
  const y1 = pieds.y - g.compagnonTaille * 0.35 - height;
  // Elle part d'où partent les pierres du décor : du haut de la montagne (`departDeLaMontagne`).
  const depart = departDeLaMontagne(i, width, g);
  return {
    x0: depart.x,
    y0: depart.y,
    x1,
    y1,
    width,
    height,
    rebonds: salve.grosBloc ? 2 : 3 - (i % 2),
    hauteurBond: height * 0.55,
  };
}

/**
 * Où en est un rocher à l'avance `p` (0 : son départ, 1 : sa cible) : il glisse le long de la
 * pente et rebondit, de moins en moins haut. Worklet : le rendu et le cadrage lisent la MÊME
 * trajectoire.
 */
export function pointDeChute(t: TrajectoireRocher, p: number): PointScene {
  'worklet';
  const x = t.x0 + (t.x1 - t.x0) * p;
  const pente = t.y0 + (t.y1 - t.y0) * p;
  // Pas de rebond au départ : il le ferait ressortir du cadre au moment où il y entre.
  const bond =
    Math.abs(Math.sin(p * Math.PI * t.rebonds)) *
    t.hauteurBond *
    Math.min(1, p * 4) *
    (1 - p * 0.6);
  return { x, y: pente - bond };
}

/**
 * Lisible si au moins 60 % du trajet se fait avec au moins 60 % du rocher dans le cadre —
 * mesuré le long de la COURBE : il entre par le haut, ou par le côté quand le compagnon est
 * déjà haut sur la pente.
 */
function chuteLisible(t: TrajectoireRocher, g: GeometrieMontee): boolean {
  const recouvre = (a: number, taille: number, borne: number) =>
    Math.max(0, Math.min(a + taille, borne) - Math.max(a, 0)) / taille;
  const N = 40;
  let vus = 0;
  for (let k = 0; k <= N; k++) {
    const q = pointDeChute(t, k / N);
    if (
      recouvre(q.x, t.width, g.sceneW) >= LISIBILITE_MINIMALE &&
      recouvre(q.y, t.height, g.sceneH) >= LISIBILITE_MINIMALE
    )
      vus++;
  }
  return vus / (N + 1) >= LISIBILITE_MINIMALE;
}

/**
 * Les invariants de cadrage de la montée (et de la descente), pour un chemin de 1 à
 * `marchesMax` marches. La chute est vérifiée depuis CHAQUE marche — c'est la plus haute qui
 * mord —, pour la salve pleine comme pour le gros bloc, sur le rocher né le plus haut.
 */
export function verifierCadrageMontee(g: GeometrieMontee, sens: SensAvalanche, marchesMax = 9) {
  const EPS = 0.5;
  let salveLisible = true;
  let grosBlocLisible = true;
  let compagnonDansLeCadre = true;
  /**
   * Chaque pierre DESCEND : elle part du sommet, plus haut qu'elle n'arrive. Sur un chemin trop
   * raide, il monte plus haut que le sommet d'où partent les pierres, et une pierre remonterait
   * la pente pour le toucher.
   */
  let pierresDescendent = true;
  const salve: Salve = { rochers: ROCHERS_PAR_SALVE_MAX, grosBloc: false };
  const bloc: Salve = { rochers: 1, grosBloc: true };
  for (let n = 1; n <= marchesMax; n++) {
    for (let k = 0; k <= n; k++) {
      const pieds = piedsALaMarche(k, n, g, sens);
      if (
        pieds.y - g.compagnonTaille < -EPS ||
        pieds.x + g.compagnonTaille / 2 > g.sceneW + EPS ||
        pieds.y > g.sceneH + EPS
      ) {
        compagnonDansLeCadre = false;
      }
      for (let i = 0; i < salve.rochers; i++) {
        const t = trajectoireRocher(i, salve, pieds, g);
        if (!chuteLisible(t, g)) salveLisible = false;
        if (t.y0 > t.y1) pierresDescendent = false;
      }
      const tb = trajectoireRocher(0, bloc, pieds, g);
      if (!chuteLisible(tb, g)) grosBlocLisible = false;
      if (tb.y0 > tb.y1) pierresDescendent = false;
    }
  }
  const but = placeDuBut(g, sens);
  return {
    salveLisible,
    grosBlocLisible,
    pierresDescendent,
    compagnonDansLeCadre,
    butDansLeCadre: but.top >= -EPS && but.left + but.width <= g.sceneW + EPS,
  };
}

export function refugePlacement(
  refuge: { ratio: number; enfoncement?: number },
  but: { left: number; top: number; width: number; height: number },
  pieds: PointScene
) {
  const h = but.height,
    w = h * refuge.ratio;
  const pied = but.top + but.height * 0.92;
  return {
    left: but.left >= pieds.x ? but.left : but.left + but.width - w,
    top: pied - h * (1 - (refuge.enfoncement ?? 0)),
    width: w,
    height: h,
  };
}
